import React, { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import ImmersiveSearch from 'components/search-bar/immersive-search';
import AdvancedSearch from 'components/search-bar/advanced-search';
import { useIsMobile } from 'hooks/use-size-class';
import BackIcon from 'components/atomic/atoms/icons/back-icon';
import styles from './style.module.scss';
import globeStyles from './globe/style.module.scss';

// Globe uses WebGL — must be client-side only
const GlobeVisualization = dynamic(() => import('./globe'), {
  ssr: false,
  loading: () => <div className={globeStyles.placeholder} />,
});

// Globe height per breakpoint (width is always 100% of the viewport)
const GLOBE_DESKTOP_HEIGHT = 520;
const GLOBE_MOBILE_HEIGHT = 420;

const GLOBE_HERO_VISIBLE_CLASS = 'globe-hero-visible';

/**
 * Hero section for the zoocasa-light homepage.
 *
 * Composes a decorative 3D globe (showing Canadian provinces)
 * with an immersive search bar that overlaps the bottom of the globe.
 *
 * The background of the nav header is transparent initially to provide more space for the globe to expand. 
 * When user scrolls page down, below the globe section, 
 * we use an intersection observer to detect this and set the background to white, 
 * so the header is opaque and doesn't pass through other sections. 
 */
export default function GlobeHeroSection() {
  const isMobile = useIsMobile();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => document.body.classList.toggle(GLOBE_HERO_VISIBLE_CLASS, entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(section);
    return () => {
      observer.disconnect();
      document.body.classList.remove(GLOBE_HERO_VISIBLE_CLASS);
    };
  }, []);

  const globeHeight = isMobile ? GLOBE_MOBILE_HEIGHT : GLOBE_DESKTOP_HEIGHT;

  return (
    <section ref={sectionRef} className={styles['hero-section']} data-hero-section>
      <div className={styles['globe-wrapper']}>
        <GlobeVisualization height={globeHeight} />
      </div>
      {isMobile
        ? <div className={`${styles['zoocasa-light-search']} ${styles['search-wrapper']}`}>
          <div
            data-globe-scroll-zone
            className={styles['search-outer']}
          >
            <div className={styles['search-inner']}>
              <ImmersiveSearch
                fullWidth
                includeSearchIcon
                forceModal
                className={styles['zoocasa-light-search-input']}
                closeButtonPlacement="left"
                closeButtonTrigger={<BackIcon />}
                overlayClassName={styles['search-overlay']}
              />
            </div>
          </div>
        </div>
        : <div className={styles['search-wrapper']}>
          <div className={styles['search-outer']}>
            <div className={styles['search-inner']}>
              <AdvancedSearch
                isFilterButtonHidden
                showsSearchOptions={false}
                className={styles['hero-search-root']}
                searchWrapperClassName={styles['hero-search-wrapper']}
                suggestedDropdownClassName={styles['hero-search-dropdown']}
                searchButtonClassName={styles['hero-search-button']}
              />
            </div>
          </div>
        </div>
      }
    </section>
  );
}
